import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import Toggle from '../ui/Toggle';
import { Btn, Field, Input, Select, FormGrid } from '../ui/primitives';
import { LabelWithHelp } from '../ui/FieldHelp';
import { ASSESSMENT_SETTINGS_FIELD_HELP as HELP } from './assessmentSettingsFieldHelp';
import { ButtonSpinnerSlot } from '../ui/ButtonSpinner';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardNavbar from '../dashboardNavbar';
import IndividualAssignmentDialog from './IndividualAssignmentDialog';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const FormRoot = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoCard = styled.div`
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.rLg};
  background: ${({ theme }) => theme.accentSoft};
  border: 1px solid ${({ theme }) => theme.accentLine};
`;

const InfoMeta = styled.p`
  margin: 0 0 6px;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 600;
  color: ${({ theme }) => theme.textMuted};
`;

const InfoText = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.5;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionLabel = styled.h4`
  margin: 0;
  font-family: ${({ theme }) => theme.fontHead};
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.text};
`;

const GridField = styled(Field)`
  margin-bottom: 0;
  min-width: 0;
`;

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ToggleWrap = styled.div`
  display: flex;
  align-items: center;
  min-height: ${({ theme }) => theme.controlH};
`;

const InlineRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InlineFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.border};
`;

const PageShell = styled.div`
  width: 100%;
`;

const PageCard = styled.div`
  max-width: 680px;
  margin: 24px auto;
  padding: 24px;
  border-radius: ${({ theme }) => theme.rLg};
  background: ${({ theme }) => theme.surface};
  border: 1px solid ${({ theme }) => theme.border};
  box-shadow: ${({ theme }) => theme.shadowSm};
`;

const ASSESSMENT_TYPES = ['Sample', 'Assessment', 'Final Assessment', 'Proctor Assessment'];

const LANGUAGES = [
  'None', 'Akan', 'Arabic', 'Chinese', 'English', 'Español', 'Français',
  'German', 'Hindi', 'Japanese', 'Korean', 'Portuguese',
];

export function AssessmentSettingForm({
  testDetails: propTestDetails,
  onDismiss,
  onSaved,
  formId = 'assessment-settings-form',
  onActionStateChange,
  showInlineFooter = true,
  saveDisabled = false,
}) {
  const { isLocked, guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const location = useLocation();
  const navigate = useNavigate();
  const routeTestDetails = location.state?.testDetails;
  const testDetails = propTestDetails || routeTestDetails;
  const hasAssessmentPrice = Number(testDetails?.price || 0) > 0;
  const [assessmentTime, setAssessmentTime] = useState(testDetails?.expiresIn || '');
  const [assessmentType, setAssessmentType] = React.useState('');
  const [originalAssessmentType, setOriginalAssessmentType] = React.useState('');
  const [loading, setLoading] = useState(false);
  const [startdate, setStartDate] = useState(
    testDetails?.startDate
      ? new Date(parseInt(testDetails.startDate, 10)).toISOString().substring(0, 10)
      : '',
  );
  const [enddate, setEndDate] = useState(
    testDetails?.endDate
      ? new Date(parseInt(testDetails.endDate, 10)).toISOString().substring(0, 10)
      : '',
  );
  const [enabled, setEnabled] = useState(testDetails?.active === 1);
  const [appPurchase, setAppPurchase] = useState(hasAssessmentPrice);
  const [price, setPrice] = useState(testDetails?.price || '');
  const [alertOpen, setAlertOpen] = useState(false);
  const [missingDetailsAlert, setMissingDetailsAlert] = useState(false);
  const [updateAlertOpen, setUpdateAlertOpen] = useState(false);
  const [enablePracticeMode, setEnablePracticeMode] = useState(testDetails?.enablePracticeMode || 0);
  const [enableHints, setEnableHints] = useState(testDetails?.enableHints || 0);
  const [enableKeywords, setEnableKeywords] = useState(testDetails?.enableKeywords || 0);
  const [enableFlashcard, setEnableFlashcard] = useState(testDetails?.enableFlashcard || 0);
  const [proctorInfo, setProctorInfo] = useState(null);
  const [hideFields, setHideFields] = useState(false);
  const [language, setLanguage] = useState(testDetails?.language || 'None');
  const [individualAssignment, setIndividualAssignment] = useState(testDetails?.individualAssignment || 0);
  const [openIndividualDialog, setOpenIndividualDialog] = useState(false);
  const [userType, setUserType] = useState(null);
  const inAppPurchaseEnable = localStorage.getItem('inAppPurchaseEnable');
  // In-app purchase is available to all users. Stripe setup (`inAppPurchaseEnable`)
  // still gates whether the toggle can actually be turned on.
  const showInAppPurchase = true;

  const userId = localStorage.getItem('userId');
  const apiUrl = process.env.REACT_APP_API_URL;

  const courseName =
    testDetails?.courseName
    || testDetails?.course?.name
    || testDetails?.course_name
    || 'No course selected';

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  const handleIndividualAssignmentChange = (e) => {
    const checked = e.target.checked;
    setIndividualAssignment(checked ? 1 : 0);
    if (checked) {
      setOpenIndividualDialog(true);
    }
  };

  const handleCloseIndividualDialog = () => {
    setOpenIndividualDialog(false);
  };

  useEffect(() => {
    const storedUserType = localStorage.getItem('userType');
    setUserType(parseInt(storedUserType, 10) || null);

    if (!testDetails) {
      setMissingDetailsAlert(true);
    } else {
      async function fetchProctorTestInfo() {
        try {
          const response = await axios.get(`${apiUrl}/api/proctor/${userId}`, {
            headers: {
              'Content-Type': 'application/json',
              Accept: '*/*',
              'Access-Control-Allow-Origin': '*',
              Authorization: getBasicAuthHeader(),
            },
          });
          setProctorInfo(response.data);
        } catch (error) {
          // legacy page swallowed errors silently
        }
      }

      async function fetchCoursePrice() {
        try {
          const response = await axios.get(`${apiUrl}/api/coursePrice/${testDetails.courseId}`, {
            headers: {
              'Content-Type': 'application/json',
              Accept: '*/*',
              'Access-Control-Allow-Origin': '*',
              Authorization: getBasicAuthHeader(),
            },
          });
          if (response.data) {
            setHideFields(true);
          } else {
            setHideFields(false);
          }
        } catch (error) {
          // legacy page swallowed errors silently
        }
      }

      const fetchAssessmentType = async () => {
        setLoading(true);
        try {
          const response = await axios.get(`${apiUrl}/api/assessmentType/${testDetails.testId}`, {
            headers: {
              'Content-Type': 'application/json',
              Accept: '*/*',
              'Access-Control-Allow-Origin': '*',
              Authorization: getBasicAuthHeader(),
            },
          });
          setAssessmentType(response.data || '');
          setOriginalAssessmentType(response.data || '');
        } catch (error) {
          // legacy page swallowed errors silently
        } finally {
          setLoading(false);
        }
      };
      fetchAssessmentType();
      fetchProctorTestInfo();
      fetchCoursePrice();
    }
  }, [testDetails, userId, apiUrl]);

  useEffect(() => {
    if (onActionStateChange) {
      onActionStateChange({ isLoading: loading });
    }
  }, [loading, onActionStateChange]);

  const handleAppPurchaseChange = (e) => {
    if (!inAppPurchaseEnable) {
      setAlertOpen(true);
      return;
    }

    const isChecked = e.target.checked;
    setAppPurchase(isChecked);

    if (!isChecked) {
      setPrice(0);
    }
  };

  const handlelanguageChange = (event) => {
    setLanguage(event.target.value);
  };

  const handlePriceChange = (event) => {
    const value = event.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setPrice(value);
    }
  };

  const handleDateChange = (e) => {
    const { id, value } = e.target;
    if (id === 'start_date') setStartDate(value);
    if (id === 'end_date') setEndDate(value);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (saveDisabled) return;
    if (!testDetails) {
      setMissingDetailsAlert(true);
      return;
    }

    if (!assessmentTime || !startdate || !enddate || !assessmentType) {
      alert('Please fill all mandatory fields before submitting.');
      return;
    }
    setLoading(true);

    const startTimestamp = new Date(startdate).getTime().toString();
    const endTimestamp = new Date(enddate).getTime().toString();
    const activeStatus = enabled ? 1 : 0;

    const bodyques = {
      testId: testDetails?.testId || '',
      startDate: startTimestamp,
      endDate: endTimestamp,
      // When the parent course has an in-app purchase price, every assessment under
      // it is free — no per-assessment in-app purchase, price forced to 0.
      price: hideFields ? 0 : (appPurchase && price ? price : 0),
      expiresIn: assessmentTime,
      active: activeStatus,
      enablePracticeMode: enablePracticeMode ? 1 : 0,
      enableKeywords: enableKeywords ? 1 : 0,
      enableHints: enableHints ? 1 : 0,
      language,
      individualAssignment,
      enableFlashcard: enableFlashcard ? 1 : 0,
    };

    const headers = {
      'Content-Type': 'application/json',
      Accept: '*/*',
      'Access-Control-Allow-Origin': '*',
      Authorization: getBasicAuthHeader(),
    };

    try {
      // Both updates must succeed; don't claim success (or run the second call)
      // if the settings update fails, and surface the real reason on error.
      await axios.put(`${apiUrl}/api/update-assessment-settings/${testDetails.testId}`, bodyques, { headers });
      await axios.put(`${apiUrl}/api/assessmentType/${testDetails.testId}`, assessmentType, { headers });
      setOriginalAssessmentType(assessmentType);
      if (onSaved) {
        onSaved();
      } else {
        setUpdateAlertOpen(true);
      }
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;
      let message = 'Could not update assessment settings. Please try again.';
      if (status === 402) {
        message = 'In-app purchase is not available on your current plan.';
      } else if (data && typeof data === 'object' && data.message) {
        message = data.message;
      } else if (typeof data === 'string' && data) {
        message = data;
      }
      console.error('Update assessment settings failed:', error);
      alert(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  const handleStripeOnboarding = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const email = localStorage.getItem('email');
    const apiUrl1 = process.env.REACT_APP_API_URL1;
    const body = { email };
    try {
      const response = await axios.post(`${apiUrl1}/api/user/create-account-and-onboarding-link`, body, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: token,
          'X-Api-Version': 'v2',
        },
      });
      window.location.href = response.data.accountLink.url;
    } catch (error) {
      // legacy page swallowed errors silently
    } finally {
      setLoading(false);
    }
  };

  const handleCloseMissingDetailsAlert = () => {
    setMissingDetailsAlert(false);
    if (onDismiss) onDismiss();
    else navigate('/Portal/Instructor/Assignment');
  };

  const handleCloseUpdateAlert = () => {
    setUpdateAlertOpen(false);
    if (onDismiss) onDismiss();
    else navigate('/Portal/Instructor/Assignment');
  };

  const showProctorType = proctorInfo
    && proctorInfo.planId > 1
    && proctorInfo.numberOfProctorAllowed - proctorInfo.numberOfProctorTests > 0;

  return (
    <>
      <IndividualAssignmentDialog
        open={openIndividualDialog}
        onClose={handleCloseIndividualDialog}
        testId={testDetails?.testId}
      />

      <FormRoot id={formId} onSubmit={handleFormSubmit}>
        <InfoCard>
          <InfoMeta>Course: {courseName}</InfoMeta>
          <InfoText>
            Define the assessment schedule, pricing, type, and visibility settings below.
          </InfoText>
        </InfoCard>

        <Section>
          <SectionLabel>Schedule</SectionLabel>
          <ScheduleGrid>
            <GridField>
              <label htmlFor="start_date"><LabelWithHelp help={HELP.startDate}>Start date</LabelWithHelp></label>
              <Input
                id="start_date"
                type="date"
                value={startdate}
                onChange={handleDateChange}
                required
              />
            </GridField>
            <GridField>
              <label htmlFor="end_date"><LabelWithHelp help={HELP.endDate}>End date</LabelWithHelp></label>
              <Input
                id="end_date"
                type="date"
                value={enddate}
                onChange={handleDateChange}
                required
              />
            </GridField>
            <GridField>
              <label htmlFor="assessmentTime"><LabelWithHelp help={HELP.expiresInDays}>Expires in (days)</LabelWithHelp></label>
              <Input
                id="assessmentTime"
                type="number"
                min="0"
                value={assessmentTime}
                onChange={(ev) => setAssessmentTime(ev.target.value)}
                placeholder="Enter assessment time"
                required
              />
            </GridField>
          </ScheduleGrid>
        </Section>

        {!hideFields && showInAppPurchase && (
          <Section>
            <SectionLabel>Pricing</SectionLabel>
            <FormGrid>
              <GridField>
                <label htmlFor="appPurchase"><LabelWithHelp help={HELP.inAppPurchase}>In-app purchase</LabelWithHelp></label>
                <ToggleWrap>
                  <Toggle
                    checked={appPurchase}
                    onChange={(v) => handleAppPurchaseChange({ target: { checked: v } })}
                  />
                </ToggleWrap>
              </GridField>
              <GridField>
                <label htmlFor="price"><LabelWithHelp help={HELP.price}>Price</LabelWithHelp></label>
                <Input
                  id="price"
                  type="text"
                  value={price}
                  onChange={handlePriceChange}
                  readOnly={!appPurchase}
                  min={0}
                />
              </GridField>
            </FormGrid>
          </Section>
        )}

        <Section>
          <SectionLabel>Assessment details</SectionLabel>
          <FormGrid>
            <GridField>
              <label htmlFor="assessmentType"><LabelWithHelp help={HELP.assessmentType}>Assessment type</LabelWithHelp></label>
              <Select
                id="assessmentType"
                value={assessmentType}
                onChange={(ev) => setAssessmentType(ev.target.value)}
                disabled={loading}
                required
              >
                <option value="" disabled>Select type</option>
                {ASSESSMENT_TYPES.filter((type) => type !== 'Proctor Assessment' || showProctorType).map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Select>
            </GridField>
            <GridField>
              <label htmlFor="Language">
                <LabelWithHelp help={HELP.language}>Language</LabelWithHelp>
              </label>
              <Select
                id="Language"
                value={language || ''}
                onChange={handlelanguageChange}
                required
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </Select>
            </GridField>
          </FormGrid>
        </Section>

        <Section>
          <SectionLabel>Visibility &amp; features</SectionLabel>
          <InlineRow>
            <GridField>
              <label htmlFor="visible"><LabelWithHelp help={HELP.visible}>Visible</LabelWithHelp></label>
              <ToggleWrap>
                <Toggle checked={enabled} onChange={(v) => setEnabled(v)} />
              </ToggleWrap>
            </GridField>

            <GridField>
              <label htmlFor="practiceMode"><LabelWithHelp help={HELP.practiceMode}>Enable practice mode</LabelWithHelp></label>
              <ToggleWrap>
                <Toggle
                  checked={enablePracticeMode === 1}
                  onChange={(v) => {
                    const newValue = v ? 1 : 0;
                    setEnablePracticeMode(newValue);
                    if (newValue === 0) {
                      setEnableHints(0);
                      setEnableKeywords(0);
                    }
                  }}
                />
              </ToggleWrap>
            </GridField>

            {enablePracticeMode === 1 && (
              <>
                <GridField>
                  <label htmlFor="enableHints"><LabelWithHelp help={HELP.enableHints}>Enable hints</LabelWithHelp></label>
                  <ToggleWrap>
                    <Toggle
                      checked={enableHints === 1}
                      onChange={(v) => setEnableHints(v ? 1 : 0)}
                    />
                  </ToggleWrap>
                </GridField>
                <GridField>
                  <label htmlFor="enableKeywords"><LabelWithHelp help={HELP.enableKeywords}>Enable keywords</LabelWithHelp></label>
                  <ToggleWrap>
                    <Toggle
                      checked={enableKeywords === 1}
                      onChange={(v) => setEnableKeywords(v ? 1 : 0)}
                    />
                  </ToggleWrap>
                </GridField>
              </>
            )}

            <GridField>
              <label htmlFor="enableFlashcard"><LabelWithHelp help={HELP.enableFlashcard}>Enable flash card</LabelWithHelp></label>
              <ToggleWrap>
                <Toggle
                  checked={enableFlashcard}
                  onChange={(v) => setEnableFlashcard(v)}
                />
              </ToggleWrap>
            </GridField>

            <GridField>
              <label htmlFor="individualAssignment"><LabelWithHelp help={HELP.individualAssignment}>Individual assignment</LabelWithHelp></label>
              <ToggleWrap>
                <Toggle
                  checked={individualAssignment === 1}
                  onChange={(v) => handleIndividualAssignmentChange({ target: { checked: v } })}
                />
              </ToggleWrap>
            </GridField>

            {individualAssignment === 1 && (
              <GridField>
                <label><LabelWithHelp help={HELP.participants}>Participants</LabelWithHelp></label>
                <ToggleWrap>
                  <Btn
                    type="button"
                    $variant="ghost"
                    onClick={() => {
                      if (!guardAction('action.assessment.settings.individualAssign')) return;
                      setOpenIndividualDialog(true);
                    }}
                  >
                    View/add individual participant
                  </Btn>
                </ToggleWrap>
              </GridField>
            )}
          </InlineRow>
        </Section>

        {showInlineFooter && (
          <InlineFoot>
            {onDismiss && (
              <Btn type="button" $variant="ghost" onClick={onDismiss} disabled={loading}>
                Cancel
              </Btn>
            )}
            <Btn type="submit" $variant="subtle" disabled={loading}>
              {loading ? 'Saving…' : 'Save changes'}
            </Btn>
          </InlineFoot>
        )}
      </FormRoot>

      <Dialog open={alertOpen} onClose={handleCloseAlert}>
        <DialogTitle>Complete Stripe setup</DialogTitle>
        <DialogContent>
          <DialogContentText>
            To enable in-app purchases, you need to complete stripe account setup first.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAlert} color="primary">Cancel</Button>
          <Button onClick={handleStripeOnboarding} color="primary" autoFocus disabled={loading}>
            {loading ? <ButtonSpinnerSlot size={24} /> : 'Complete Stripe setup'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={missingDetailsAlert} onClose={handleCloseMissingDetailsAlert}>
        <DialogTitle>Select assessment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please select an assessment to continue.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMissingDetailsAlert} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={updateAlertOpen} onClose={handleCloseUpdateAlert}>
        <DialogTitle>Assessment updated</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The assessment has been successfully updated.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUpdateAlert} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}

export default function AssessmentForm() {
  const { isLocked, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const location = useLocation();
  const navigate = useNavigate();
  const { testDetails } = location.state || {};
  const menuItems = [
    { title: 'Assessments', link: '/Portal/Instructor/Assignment' },
    { title: 'Questions', link: '/Portal/Instructor/Questions' },
  ];

  return (
    <>
      <DashboardNavbar menuItems={menuItems} />
      <PageShell>
        <PageCard>
          <AssessmentSettingForm
            testDetails={testDetails}
            onDismiss={() => navigate('/Portal/Instructor/Assignment')}
            showInlineFooter
            saveDisabled={isLocked('action.assessment.settings.save')}
          />
        </PageCard>
      </PageShell>
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
}
